/**
 * Shared toggle component for OR/AND filter mode switches with optional clear action.
 */

import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { X } from 'lucide-react';
import { useId, type ReactNode } from 'react';

interface FilterModeToggleProps {
  filterMode: 'OR' | 'AND';
  onFilterModeChange: (mode: 'OR' | 'AND') => void;
  onClearSelection?: () => void;
  isClearDisabled?: boolean;
  clearAriaLabel?: string;
  orTooltip?: string;
  andTooltip?: string;
  switchId?: string;
}

const OR_LABEL = 'OR';
const AND_LABEL = 'AND';

const tooltipContent = (label: ReactNode, tooltip?: string) => {
  if (!tooltip) {
    return label;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{label}</TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export function FilterModeToggle({
  filterMode,
  onFilterModeChange,
  onClearSelection,
  isClearDisabled = false,
  clearAriaLabel = 'Clear selection',
  orTooltip,
  andTooltip,
  switchId,
}: FilterModeToggleProps) {
  const generatedId = useId();
  const controlId = switchId ?? `${generatedId}-filter-mode`;
  const orLabelClass =
    filterMode === OR_LABEL ? 'text-xs font-bold' : 'text-xs text-muted-foreground';
  const andLabelClass =
    filterMode === AND_LABEL ? 'text-xs font-bold' : 'text-xs text-muted-foreground';

  const orLabel = tooltipContent(<span className={orLabelClass}>{OR_LABEL}</span>, orTooltip);
  const andLabel = tooltipContent(<span className={andLabelClass}>{AND_LABEL}</span>, andTooltip);

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1 rounded-md border px-2 py-1">
        {orLabel}
        <Switch
          id={controlId}
          checked={filterMode === AND_LABEL}
          onCheckedChange={checked => onFilterModeChange(checked ? AND_LABEL : OR_LABEL)}
          className="scale-75"
        />
        {andLabel}
      </div>
      {onClearSelection && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={onClearSelection}
          disabled={isClearDisabled}
          aria-label={clearAriaLabel}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

